/**
 * A short string that may contain mathematics — figure titles, axis labels,
 * legend entries, annotations, captions.
 *
 * These live outside the markdown pipeline, so they get their own renderer.
 * They also live outside SVG: an SVG `<text>` node can only hold a string, so
 * a legend entry drawn there shows `sin(t)e^{-\sigma t}` literally. Rendering
 * figure chrome as HTML instead is what lets it be typeset.
 */

import { useMemo, type CSSProperties } from "react";
import katex from "katex";

interface MathTextProps {
  readonly children: string;
  readonly className?: string;
  /** For overlay labels, which are positioned against the figure. */
  readonly style?: CSSProperties;
}

export function MathText({ children, className, style }: MathTextProps) {
  const segments = useMemo(() => toSegments(children), [children]);

  return (
    <span className={className} style={style}>
      {segments.map((segment, index) =>
        segment.math ? (
          <span
            key={index}
            // KaTeX emits its own markup; it is generated here, not user HTML.
            dangerouslySetInnerHTML={{ __html: renderMath(segment.text) }}
          />
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </span>
  );
}

interface Segment {
  readonly text: string;
  readonly math: boolean;
}

/**
 * Split a label into plain and mathematical runs.
 *
 * Explicit `$…$` wins. Failing that, a label that is *obviously* TeX gets
 * typeset whole — models write `e^{-\sigma t}` in a label far more often than
 * they remember to delimit it, and the alternative is showing the reader
 * braces and backslashes.
 */
export function toSegments(raw: string): readonly Segment[] {
  const text = raw.trim();
  if (text.length === 0) return [];

  if (text.includes("$")) {
    return text
      .split(/\$([^$]*)\$/g)
      .map((part, index) => ({ text: part, math: index % 2 === 1 }))
      .filter((segment) => segment.text.length > 0);
  }

  // The undelimited fallback is only safe for labels. A sentence containing
  // `F_{\mu\nu}` is prose with maths in it, not an equation, and typesetting
  // the whole thing collapses every word space and renders it unreadable.
  return looksLikeTex(text) && isLabelLike(text)
    ? [{ text, math: true }]
    : [{ text, math: false }];
}

/**
 * TeX markup that has no business in prose: a control sequence, a braced
 * group, or a sub/superscript. A bare `E (N/C)` or `time (s)` stays text.
 */
export function looksLikeTex(text: string): boolean {
  return /\\[a-zA-Z]+|[_^]\{|\^[0-9a-zA-Z]|_[0-9a-zA-Z]|\{[^}]*\}/.test(text);
}

/** Longest a bare label can plausibly be before it is really a sentence. */
const MAX_LABEL_LENGTH = 60;
const MAX_LABEL_WORDS = 8;

/**
 * Is this a *label* — an axis title, a legend entry, a plot annotation — as
 * opposed to a sentence that happens to mention some mathematics?
 *
 * Sentence punctuation and length are the giveaways. Anything failing this is
 * rendered as text, so undelimited maths inside it shows as source: the fix for
 * that is the author writing `$…$`, which every agent prompt now requires.
 */
export function isLabelLike(text: string): boolean {
  if (text.length > MAX_LABEL_LENGTH) return false;
  // Mid-string sentence punctuation means at least one clause has ended.
  if (/[.!?,;:]\s/.test(text)) return false;
  return text.split(/\s+/).length <= MAX_LABEL_WORDS;
}

const KATEX_OPTIONS = {
  throwOnError: false,
  displayMode: false,
  output: "html" as const,
  strict: false as const,
  trust: false,
};

function renderMath(tex: string): string {
  try {
    return katex.renderToString(tex, KATEX_OPTIONS);
  } catch {
    // A label is never worth failing a figure over.
    return escapeHtml(tex);
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
